<template>
  <div class="author-application-page">
    <div class="page-header">
      <h1>
        <svg class="title-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
        作者认证
      </h1>
      <p>成为认证作者，开启您的创作之旅</p>
    </div>

    <!-- 未登录提示 -->
    <div v-if="!isLoggedIn" class="login-required">
      <div class="login-prompt">
        <h3>需要登录</h3>
        <p>作者认证需要登录后才能申请，请先登录您的账户。</p>
        <button @click="goToLogin" class="btn-primary">前往登录</button>
      </div>
    </div>

    <div v-else-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>加载中...</p>
    </div>

    <!-- 已经是作者 -->
    <div v-else-if="isAuthor" class="status-card approved">
      <div class="status-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      </div>
      <h2>您已是认证作者</h2>
      <p>恭喜您已通过作者认证，现在可以前往投稿页面发布作品了。</p>
      <div class="status-actions">
        <button @click="goToSubmission" class="btn-primary">去投稿</button>
        <button @click="goToProfile" class="btn-secondary">返回个人中心</button>
      </div>
    </div>

    <!-- 审核中 -->
    <div v-else-if="authorStatus === 'pending'" class="status-card pending">
      <div class="status-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
      </div>
      <h2>审核中</h2>
      <p>您的作者认证申请已提交，审核通常需要 1-3 个工作日，请耐心等待。</p>
      <button @click="goToProfile" class="btn-secondary">返回个人中心</button>
    </div>

    <!-- 审核拒绝 -->
    <div v-else-if="authorStatus === 'rejected'" class="status-card rejected">
      <div class="status-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
      </div>
      <h2>审核未通过</h2>
      <p v-if="applicationRemark" class="remark">原因：{{ applicationRemark }}</p>
      <p>您可以补充资料后重新提交申请。</p>
    </div>

    <!-- 申请表单 -->
    <div v-if="isLoggedIn && !isAuthor && authorStatus !== 'pending'" class="application-form">
      <div class="form-section">
        <h2>
          <svg class="title-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>
          填写认证信息
        </h2>

        <form @submit.prevent="submitApplication">
          <div class="form-row">
            <div class="form-group">
              <label for="realname">真实姓名 *</label>
              <input
                type="text"
                id="realname"
                v-model="form.realname"
                placeholder="请输入真实姓名"
                maxlength="20"
                required
              />
            </div>

            <div class="form-group">
              <label for="idcard">身份证号 *</label>
              <input
                type="text"
                id="idcard"
                v-model="form.idcard"
                placeholder="请输入身份证号"
                maxlength="18"
                required
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="mobile">联系电话</label>
              <input
                type="tel"
                id="mobile"
                v-model="form.mobile"
                placeholder="请输入常用手机号"
                maxlength="11"
              />
            </div>

            <div class="form-group">
              <label for="email">联系邮箱</label>
              <input
                type="email"
                id="email"
                v-model="form.email"
                placeholder="请输入常用邮箱"
                maxlength="100"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="reason">申请理由 / 代表作品</label>
            <textarea
              id="reason"
              v-model="form.reason"
              placeholder="请简要介绍您的创作经历、代表作品或申请理由..."
              rows="4"
              maxlength="500"
            ></textarea>
            <span class="char-count">{{ form.reason.length }}/500</span>
          </div>

          <div class="form-tip">
            提交申请即表示您承诺所填信息真实有效，审核通过后可在本平台发布作品。
          </div>

          <div class="form-actions">
            <button type="submit" class="btn-primary" :disabled="isSubmitting">
              {{ isSubmitting ? '提交中...' : '提交认证申请' }}
            </button>
            <button type="button" @click="goToProfile" class="btn-secondary">取消</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { getAuthorStatus, applyAuthor } from '../api/index';

export default {
  name: 'AuthorApplicationPage',
  data() {
    return {
      isLoggedIn: false,
      loading: true,
      isAuthor: false,
      authorStatus: 'none',
      applicationRemark: '',
      isSubmitting: false,
      form: {
        realname: '',
        idcard: '',
        mobile: '',
        email: '',
        reason: ''
      }
    };
  },
  mounted() {
    this.checkLoginStatus();
    if (this.isLoggedIn) {
      this.loadAuthorStatus();
    } else {
      this.loading = false;
    }
  },
  methods: {
    checkLoginStatus() {
      const token = localStorage.getItem('userToken');
      this.isLoggedIn = !!token;
    },

    async loadAuthorStatus() {
      this.loading = true;
      try {
        const res = await getAuthorStatus();
        if (res.data && res.data.code === 1) {
          const data = res.data.data || {};
          this.isAuthor = !!data.is_author;
          if (data.application) {
            this.authorStatus = data.application.status || 'none';
            this.applicationRemark = data.application.remark || '';
          } else {
            this.authorStatus = 'none';
          }
        }
      } catch (e) {
        console.error('获取作者认证状态失败:', e);
      } finally {
        this.loading = false;
      }
    },

    async submitApplication() {
      if (this.isSubmitting) return;

      if (!this.form.realname.trim()) {
        alert('请输入真实姓名');
        return;
      }
      if (!this.form.idcard.trim()) {
        alert('请输入身份证号');
        return;
      }
      if (!/[\u4e00-\u9fa5]{2,20}/.test(this.form.realname)) {
        alert('真实姓名应为2-20个中文字符');
        return;
      }
      if (!/(^\d{15}$)|(^\d{17}[\dXx]$)/.test(this.form.idcard)) {
        alert('身份证号格式不正确');
        return;
      }
      if (this.form.mobile && !/^1\d{10}$/.test(this.form.mobile)) {
        alert('手机号格式不正确');
        return;
      }
      if (this.form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
        alert('邮箱格式不正确');
        return;
      }

      this.isSubmitting = true;
      try {
        const res = await applyAuthor({
          realname: this.form.realname.trim(),
          idcard: this.form.idcard.trim(),
          mobile: this.form.mobile.trim(),
          email: this.form.email.trim(),
          reason: this.form.reason.trim()
        });

        if (res.data && res.data.code === 1) {
          alert('申请已提交，请等待审核');
          this.authorStatus = 'pending';
        } else {
          alert(res.data?.msg || '提交失败');
        }
      } catch (error) {
        console.error('提交作者认证申请失败:', error);
        alert('提交失败: ' + (error.response?.data?.msg || error.message || '未知错误'));
      } finally {
        this.isSubmitting = false;
      }
    },

    goToLogin() {
      this.$router.push('/profile');
    },

    goToSubmission() {
      this.$router.push('/submission');
    },

    goToProfile() {
      this.$router.push('/profile');
    }
  }
};
</script>

<style scoped>
.author-application-page {
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: 18px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  color: var(--text);
}

.page-header {
  text-align: center;
  margin-bottom: 24px;
  padding: 22px 24px;
  background: linear-gradient(135deg, var(--brand-deep) 0%, var(--brand) 100%);
  color: #fff;
  border-radius: 8px;
}

.page-header h1 {
  margin: 0 0 8px 0;
  font-size: 22px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.page-header p {
  margin: 0;
  opacity: 0.9;
  font-size: 13px;
}

.title-svg {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.login-required {
  text-align: center;
  padding: 40px;
}

.login-prompt {
  background: var(--surface-2);
  padding: 24px;
  border-radius: 8px;
  border: 1px solid var(--border);
}

.login-prompt h3 {
  color: var(--text);
  margin-bottom: 12px;
  font-size: 16px;
}

.login-prompt p {
  color: var(--text-dim);
  margin-bottom: 16px;
  font-size: 13px;
}

.loading-state {
  text-align: center;
  padding: 40px;
  color: var(--text-dim);
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--border);
  border-top-color: var(--brand);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 12px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.status-card {
  text-align: center;
  padding: 32px 24px;
  border-radius: 8px;
  border: 1px solid var(--border);
  margin-bottom: 20px;
}

.status-card.approved {
  background: rgba(34, 197, 94, 0.08);
  border-color: var(--success);
}

.status-card.pending {
  background: rgba(251, 191, 36, 0.08);
  border-color: #fbbf24;
}

.status-card.rejected {
  background: rgba(248, 113, 113, 0.08);
  border-color: var(--danger);
}

.status-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.status-icon svg {
  width: 30px;
  height: 30px;
}

.status-card.approved .status-icon {
  background: rgba(34, 197, 94, 0.15);
  color: var(--success);
}

.status-card.pending .status-icon {
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
}

.status-card.rejected .status-icon {
  background: rgba(248, 113, 113, 0.15);
  color: var(--danger);
}

.status-card h2 {
  margin: 0 0 10px 0;
  font-size: 18px;
  color: var(--text);
}

.status-card p {
  margin: 0 0 16px 0;
  color: var(--text-dim);
  font-size: 13px;
}

.status-card .remark {
  color: var(--danger);
  font-weight: 500;
}

.status-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.form-section {
  background: var(--surface-2);
  border: 1px solid var(--border);
  padding: 20px;
  border-radius: 8px;
}

.form-section h2 {
  margin: 0 0 18px 0;
  color: var(--text);
  font-size: 17px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 7px;
}

.form-group {
  margin-bottom: 16px;
  position: relative;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  color: var(--text);
  font-weight: 500;
  font-size: 13px;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 9px 12px;
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 13px;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 2px var(--brand-soft);
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.form-group textarea {
  resize: vertical;
  min-height: 86px;
}

.char-count {
  position: absolute;
  right: 10px;
  bottom: -16px;
  font-size: 11px;
  color: var(--text-dim);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

.form-tip {
  background: var(--brand-soft);
  color: var(--brand);
  font-size: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 18px;
}

.form-actions {
  text-align: center;
}

.btn-primary {
  background: var(--brand);
  color: white;
  border: none;
  padding: 9px 22px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0 6px;
}

.btn-primary:hover:not(:disabled) {
  background: var(--brand-hover);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--surface-3);
  color: var(--text);
  border: 1px solid var(--border);
  padding: 9px 22px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0 6px;
}

.btn-secondary:hover {
  background: var(--brand-soft);
  border-color: var(--brand);
  transform: translateY(-1px);
}

@media (max-width: 768px) {
  .author-application-page {
    margin: 10px;
    padding: 15px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
